import type { UseFormSetValue } from 'react-hook-form';
import { Address } from '@legacy/shared-types/dto/address.dto';
import { FCAOrganisationDetails, ExistingFirmResponse } from '@api/firm/firm.types';
import { Network } from '@api/network/network.types';
import { MortgageClub } from '@api/mortgage-club/mortgageClub.types';
type BackendAddress = Address & { town?: string };
import { CreateAddress, IAddressResponseDetails } from '@app-types/address';
import { OrganisationAddressForm, EMPTY_ORGANISATION_ADDRESS } from './types/address';
import { SignupOrganisationFormFields } from './signupOrganisationForm.types';
import { FcaResultKind } from './fcaResultKind';
import type { ClubFcaResult, FirmFcaResult, NetworkFcaResult } from './fcaLookup';
import {
  brokerOrgFormRoot as G,
  type BrokerOrgFormRootKey,
  firmField,
  formPath,
  networkField,
  orgField,
  orgAddressValuePath,
} from './formFieldPaths';

type ApplyFcaLookupPayload =
  | { section: 'firm'; result: FirmFcaResult }
  | { section: 'network'; result: NetworkFcaResult }
  | { section: 'mortgageClub'; result: ClubFcaResult };

export function mapPostcodeAddressToForm(addr: IAddressResponseDetails): OrganisationAddressForm {
  return {
    addressLine1: addr.addressLine1 ?? '',
    addressLine2: addr.addressLine2 ?? '',
    postcode: addr.postcode ?? '',
    town: addr.townOrCity ?? '',
    county: addr.county ?? '',
    country: addr.country ?? '',
  };
}

export function mapBackendAddressToForm(addr: BackendAddress): OrganisationAddressForm {
  const town = addr.town ?? addr.townOrCity ?? '';
  return {
    addressLine1: addr.addressLine1 ?? '',
    addressLine2: addr.addressLine2 ?? '',
    postcode: addr.postcode ?? '',
    town,
    county: addr.county ?? '',
    country: addr.country ?? '',
  };
}

export function mapFcaRegisterToAddress(d: FCAOrganisationDetails): OrganisationAddressForm {
  return {
    addressLine1: d.address1 ?? '',
    addressLine2: d.address2 ?? '',
    postcode: d.postcode ?? '',
    town: d.city ?? '',
    county: '',
    country: d.country ?? '',
  };
}

function setRegisterFromPublicRegister(
  setValue: UseFormSetValue<SignupOrganisationFormFields>,
  root: BrokerOrgFormRootKey,
  data: FCAOrganisationDetails,
): void {
  setValue(formPath(root, orgField.backendId), null);
  setValue(formPath(root, orgField.name), data.registeredName ?? '');
  setValue(orgAddressValuePath(root), mapFcaRegisterToAddress(data));
  setValue(formPath(root, orgField.isAddressManual), true);
}

/**
 * Writes FCA lookup outcome (DB hit, public register, or not found) into the signup form for firm / network / club.
 */
export function applyFcaLookupResult(
  setValue: UseFormSetValue<SignupOrganisationFormFields>,
  { section, result: r }: ApplyFcaLookupPayload,
): void {
  if (r.kind === FcaResultKind.NotFound) {
    return;
  }

  if (r.kind === FcaResultKind.Existing) {
    if (section === 'firm') {
      applyExistingFirm(setValue, r.firm);
      return;
    }
    if (section === 'network') {
      applyExistingNetwork(setValue, r.network);
      return;
    }
    applyExistingClub(setValue, r.club);
    return;
  }

  const data = r.data;
  if (section === 'firm') {
    setRegisterFromPublicRegister(setValue, G.firm, data);
    setValue(formPath(G.firm, firmField.companyNumber), data.companyNumber ?? '');
    setValue(formPath(G.firm, firmField.website), data.website ?? '');
    setValue(formPath(G.firm, firmField.phoneNumber), data.phoneNumber ?? '');
    return;
  }
  if (section === 'network') {
    setRegisterFromPublicRegister(setValue, G.network, data);
    setValue(formPath(G.network, networkField.isStrideUpOnPanel), null);
    setValue(formPath(G.network, networkField.isPartOfMortgageClub), null);
    return;
  }
  setRegisterFromPublicRegister(setValue, G.mortgageClub, data);
}

function applyExistingFirm(setValue: UseFormSetValue<SignupOrganisationFormFields>, f: ExistingFirmResponse): void {
  setValue(formPath(G.firm, orgField.backendId), f.id);
  setValue(formPath(G.firm, orgField.name), f.name);
  setValue(formPath(G.firm, orgField.financialConductAuthorityNumber), f.fcaNumber);
  setValue(formPath(G.firm, firmField.companyNumber), f.companyNumber ?? '');
  setValue(formPath(G.firm, firmField.principalOrDirectorName), f.principalOrDirectorName ?? '');
  setValue(formPath(G.firm, firmField.phoneNumber), f.phoneNumber ?? '');
  setValue(formPath(G.firm, firmField.website), f.website ?? '');
  setValue(formPath(G.firm, firmField.informationCommissionersOfficeNumber), f.icoNumber ?? '');
  setValue(formPath(G.firm, firmField.authorisationType), f.authorisationType ?? null);
  setValue(formPath(G.firm, firmField.hasHppPermissions), f.hasHppPermissions ?? null);
  setValue(
    orgAddressValuePath(G.firm),
    f.address ? mapBackendAddressToForm(f.address) : { ...EMPTY_ORGANISATION_ADDRESS },
  );
  setValue(formPath(G.firm, orgField.isAddressManual), true);
}

function applyExistingNetwork(setValue: UseFormSetValue<SignupOrganisationFormFields>, n: Network): void {
  setValue(formPath(G.network, orgField.backendId), n.id);
  setValue(formPath(G.network, orgField.name), n.name);
  setValue(formPath(G.network, orgField.financialConductAuthorityNumber), n.fcaNumber);
  setValue(formPath(G.network, networkField.isStrideUpOnPanel), n.isStrideUpOnPanel ?? null);
  setValue(formPath(G.network, networkField.isPartOfMortgageClub), Boolean(n.mortgageClub));
  setValue(
    orgAddressValuePath(G.network),
    n.address ? mapBackendAddressToForm(n.address) : { ...EMPTY_ORGANISATION_ADDRESS },
  );
  setValue(formPath(G.network, orgField.isAddressManual), true);
}

function applyExistingClub(setValue: UseFormSetValue<SignupOrganisationFormFields>, c: MortgageClub): void {
  setValue(formPath(G.mortgageClub, orgField.backendId), c.id);
  setValue(formPath(G.mortgageClub, orgField.name), c.name);
  setValue(formPath(G.mortgageClub, orgField.financialConductAuthorityNumber), c.fcaNumber);
  setValue(
    orgAddressValuePath(G.mortgageClub),
    c.address ? mapBackendAddressToForm(c.address) : { ...EMPTY_ORGANISATION_ADDRESS },
  );
  setValue(formPath(G.mortgageClub, orgField.isAddressManual), true);
}

export function getEmptySignupForm(): SignupOrganisationFormFields {
  return {
    firm: {
      name: '',
      principalOrDirectorName: '',
      companyNumber: '',
      financialConductAuthorityNumber: '',
      informationCommissionersOfficeNumber: '',
      authorisationType: null,
      hasHppPermissions: null,
      isPartOfClub: null,
      website: '',
      phoneNumber: '',
      isAddressManual: false,
      address: { ...EMPTY_ORGANISATION_ADDRESS },
      backendId: null,
    },
    network: {
      name: '',
      financialConductAuthorityNumber: '',
      isPartOfMortgageClub: null,
      isStrideUpOnPanel: null,
      isAddressManual: false,
      address: { ...EMPTY_ORGANISATION_ADDRESS },
      backendId: null,
    },
    mortgageClub: {
      name: '',
      financialConductAuthorityNumber: '',
      isAddressManual: false,
      address: { ...EMPTY_ORGANISATION_ADDRESS },
      backendId: null,
    },
  };
}

export function toCreateNetworkAddress(addr: OrganisationAddressForm): CreateAddress {
  return {
    addressLine1: addr.addressLine1,
    addressLine2: addr.addressLine2,
    town: addr.town,
    postcode: addr.postcode,
    country: addr.country,
    county: addr.county || null,
  };
}

export function toCreateClubAddress(addr: OrganisationAddressForm) {
  return toCreateNetworkAddress(addr);
}
